import * as React from 'react';

import { Link } from '@coursera/cds-core';

import { useEnterpriseProperties } from 'bundles/account-profile/common/eventing/useEnterpriseProperties';
import Notification from 'bundles/notification-center/components/Notification';
import type {
  DegreePathwaysCompletionNudgeMessageNotification as DegreePathwaysCompletionNudgeMessageType,
  Notification as NotificationType,
} from 'bundles/notification-center/types';

import _t from 'i18n!nls/notification-center';

type Props = {
  notification: DegreePathwaysCompletionNudgeMessageType;
  rank?: number;
  onClick: (notification: NotificationType) => void;
};

export enum DegreePathwaysNotificationVariant {
  FIRST_MODULE_COMPLETION = 'FIRST_MODULE_COMPLETION',
  SECOND_MODULE_COMPLETION = 'SECOND_MODULE_COMPLETION',
  THIRD_MODULE_COMPLETION = 'THIRD_MODULE_COMPLETION',
}

const getDegreePathwaysNotificationVariantMessage = (
  variant: DegreePathwaysNotificationVariant,
  specializationName: string
) => {
  switch (variant) {
    case DegreePathwaysNotificationVariant.FIRST_MODULE_COMPLETION:
      return _t(
        "You're making progress—complete #{specializationName} and you could earn academic credit toward a degree.",
        { specializationName }
      );
    case DegreePathwaysNotificationVariant.SECOND_MODULE_COMPLETION:
      return _t(
        'Boost your earning potential—job candidates with degrees often make higher salaries. Completion of #{specializationName} could get you one step closer.',
        { specializationName }
      );
    case DegreePathwaysNotificationVariant.THIRD_MODULE_COMPLETION:
      return _t(
        "You're almost there—complete #{specializationName} and you could earn academic credit toward a degree.",
        {
          specializationName,
        }
      );
    default:
      return '';
  }
};

const DegreePathwaysCompletionNudgeNotification = ({ notification, rank, onClick }: Props) => {
  const enterpriseProperties = useEnterpriseProperties();

  const { data } = notification;
  const { degreeName, degreeId, degreeSlug, variant } = data;
  const degreeUrl =
    variant === DegreePathwaysNotificationVariant.FIRST_MODULE_COMPLETION
      ? `/degrees/${degreeSlug}`
      : `/degrees/${degreeSlug}?nextStep=emailMeInfo`;

  const specializationName = notification.data.specializationName ?? '';

  const notificationOnClickEventing = () => {
    onClick(notification);
  };

  return (
    <Notification
      href={degreeUrl}
      notification={notification}
      rank={rank}
      onClick={notificationOnClickEventing}
      trackingData={{
        product: {
          id: degreeId,
          name: degreeName,
          slug: degreeSlug,
          type: 'degree' as const,
        },
        enterprise: {
          organizationSlug: enterpriseProperties?.organizationSlug,
          programSlug: enterpriseProperties?.programSlug,
        },
      }}
    >
      {getDegreePathwaysNotificationVariantMessage(variant, specializationName)}{' '}
      <Link href={degreeUrl} variant="quiet" onClick={notificationOnClickEventing}>
        {_t('View related degree')}
      </Link>
    </Notification>
  );
};
export default DegreePathwaysCompletionNudgeNotification;
